import { useState, useEffect, useCallback } from 'react';
import type { PageData, BlockData } from '../types/editing';
import type { ActiveSpread } from './useSpreadState';
import { useWordDataContext } from '../contexts/WordDataContext';

interface UseSpreadDataProps {
  isbn: string;
  activeSpread: ActiveSpread;
  setActiveSpread: React.Dispatch<React.SetStateAction<ActiveSpread>>;
}

/**
 * Hook to load word data for both pages in the active spread
 */
export const useSpreadData = ({ 
  isbn, 
  activeSpread, 
  setActiveSpread 
}: UseSpreadDataProps) => {
  const { state: wordDataState, actions: wordDataActions } = useWordDataContext();
  const [loadingLeft, setLoadingLeft] = useState(false);
  const [loadingRight, setLoadingRight] = useState(false);
  const [errorLeft, setErrorLeft] = useState<string | null>(null);
  const [errorRight, setErrorRight] = useState<string | null>(null);

  // Determine word data filename strategy for a page
  const getWordDataStrategy = useCallback((page: PageData | null): string | null => {
    if (!page) return null;
    
    // Only load words file if text_extracted is "enhanced" for this page
    if (page.text_extracted !== 'enhanced' && page.text_extracted !== true) {
      return null;
    }
    
    // Skip pages that shouldn't have word data (based on page type)
    const skipWordDataTypes = ['blank-page', 'no-text'];
    if (page.pageType && skipWordDataTypes.includes(page.pageType)) {
      return null;
    }
    
    // Use spine.json data - only use words field
    if (page.words) {
      return page.words;
    }
    
    // Fallback to words-enhanced if available
    if (page['words-enhanced']) {
      return page['words-enhanced'];
    }
    
    return null;
  }, []);

  // Load word data for a single page
  const loadPageWordData = useCallback(async (
    pageData: PageData | null,
    pageIndex: number | null,
    isLeft: boolean
  ): Promise<{ wordData: any | null; blocks: BlockData[] }> => {
    if (!pageData || !pageIndex || !isbn) {
      return { wordData: null, blocks: [] };
    }

    const wordsFilename = getWordDataStrategy(pageData);
    if (!wordsFilename) {
      return { wordData: null, blocks: [] };
    }

    try {
      const { FolderService } = await import('../services/FolderService');
      const folder = FolderService.getFolder();
      
      if (!folder) {
        throw new Error('Folder is required but not set');
      }
      
      const { authenticatedFetch } = await import('../utils/api');
      const url = FolderService.getPageJsonUrl(isbn, wordsFilename);
      const response = await authenticatedFetch(url);
      if (!response.ok) {
        throw new Error(`Failed to load word data: ${response.statusText}`);
      }

      const wordData = await response.json();
      const blocks = wordData.blocks && Array.isArray(wordData.blocks) 
        ? wordData.blocks as BlockData[]
        : [];

      return { wordData, blocks };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`Error loading ${isLeft ? 'left' : 'right'} page word data:`, errorMessage);
      throw error;
    }
  }, [isbn, getWordDataStrategy]);

  // Load word data for left page
  useEffect(() => {
    if (!activeSpread.leftPageData || !activeSpread.leftPageIndex) {
      return;
    }

    // Check if blocks are already loaded in WordDataContext
    const existingBlocks = wordDataState.blocksByPage.get(activeSpread.leftPageIndex);
    if (existingBlocks && existingBlocks.length > 0) {
      return; // Already loaded
    }

    // Check if already loading
    if (wordDataState.loadingPages.has(activeSpread.leftPageIndex)) {
      return;
    }

    const wordsFilename = getWordDataStrategy(activeSpread.leftPageData);
    if (!wordsFilename) {
      return;
    }

    setLoadingLeft(true);
    setErrorLeft(null);

    wordDataActions.loadPageBlocks(activeSpread.leftPageIndex, isbn, wordsFilename)
      .then(() => {
        setLoadingLeft(false);
      })
      .catch((error) => {
        setErrorLeft(error instanceof Error ? error.message : 'Unknown error');
        setLoadingLeft(false);
      });
  }, [activeSpread.leftPageData, activeSpread.leftPageIndex, isbn, getWordDataStrategy, wordDataState.blocksByPage, wordDataState.loadingPages, wordDataActions]);

  // Load word data for right page
  useEffect(() => {
    if (!activeSpread.rightPageData || !activeSpread.rightPageIndex) {
      return;
    }

    // Check if blocks are already loaded in WordDataContext
    const existingBlocks = wordDataState.blocksByPage.get(activeSpread.rightPageIndex);
    if (existingBlocks && existingBlocks.length > 0) {
      return; // Already loaded
    }

    // Check if already loading
    if (wordDataState.loadingPages.has(activeSpread.rightPageIndex)) {
      return;
    }

    const wordsFilename = getWordDataStrategy(activeSpread.rightPageData);
    if (!wordsFilename) {
      return;
    }

    setLoadingRight(true);
    setErrorRight(null);

    wordDataActions.loadPageBlocks(activeSpread.rightPageIndex, isbn, wordsFilename)
      .then(() => {
        setLoadingRight(false);
      })
      .catch((error) => {
        setErrorRight(error instanceof Error ? error.message : 'Unknown error');
        setLoadingRight(false);
      });
  }, [activeSpread.rightPageData, activeSpread.rightPageIndex, isbn, getWordDataStrategy, wordDataState.blocksByPage, wordDataState.loadingPages, wordDataActions]);

  return {
    loadingLeft,
    loadingRight,
    errorLeft,
    errorRight,
    reloadLeft: () => {
      if (activeSpread.leftPageData && activeSpread.leftPageIndex) {
        const wordsFilename = getWordDataStrategy(activeSpread.leftPageData);
        if (wordsFilename) {
          wordDataActions.loadPageBlocks(activeSpread.leftPageIndex, isbn, wordsFilename);
        }
      }
    },
    reloadRight: () => {
      if (activeSpread.rightPageData && activeSpread.rightPageIndex) {
        const wordsFilename = getWordDataStrategy(activeSpread.rightPageData);
        if (wordsFilename) {
          wordDataActions.loadPageBlocks(activeSpread.rightPageIndex, isbn, wordsFilename);
        }
      }
    },
  };
};

